import { useEffect, useState } from "react";
import { Link, Redirect } from "wouter";
import { useAuth } from "../hooks/useAuth";
import { customerFetch } from "../lib/customerFetch";
import { Wordmark } from "../components/Brand";
import { BottomNav } from "../components/BottomNav";

/**
 * Customer Vouchers list — Phase 9.
 *
 * Route: /profile/vouchers
 * Cross-tenant list of every voucher the customer holds (any status).
 * Tapping a row drills into /vouchers/:id.
 */

interface VoucherRow {
  id: string;
  voucherCode: string;
  status: "ISSUED" | "REDEEMED" | "EXPIRED" | "CANCELLED";
  rewardName: string;
  tenantName: string;
  issuedAt: string;
  expiresAt: string;
}

const STATUS_STYLES: Record<VoucherRow["status"], string> = {
  ISSUED: "bg-emerald-100 text-emerald-800",
  REDEEMED: "bg-navy/10 text-navy",
  EXPIRED: "bg-ink-light text-ink-dark",
  CANCELLED: "bg-red-100 text-red-800",
};

export function VouchersPage() {
  const { isAuthenticated } = useAuth();
  const [rows, setRows] = useState<VoucherRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    void (async () => {
      const r = await customerFetch<VoucherRow[]>(
        "/api/v1/customer/me/redemptions",
      );
      if (cancelled) return;
      if (!r.ok) setErr(r.message);
      else setRows(r.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  if (!isAuthenticated) return <Redirect to="/login" />;

  return (
    <div className="min-h-screen pb-28">
      <div className="mx-auto max-w-md px-6 pt-8">
        <header className="flex items-center justify-between">
          <Wordmark size="sm" />
          <Link href="/profile" className="text-sm text-ink-dark hover:text-navy">
            ← Profile
          </Link>
        </header>

        <h1 className="mt-6 text-2xl font-bold text-navy">My vouchers</h1>

        {err && (
          <div className="mt-4 rounded-card border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {err}
          </div>
        )}

        {rows === null ? (
          <p className="mt-4 text-sm text-ink-dark">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="mt-6 card text-center">
            <p className="text-sm text-ink-dark">
              No vouchers yet. Earn points or stamps and redeem them on a
              merchant's card detail page.
            </p>
          </div>
        ) : (
          <ul className="mt-4 space-y-2">
            {rows.map((v) => (
              <li key={v.id}>
                <Link
                  href={`/vouchers/${v.id}`}
                  className="card block hover:border-navy/30"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
                        {v.tenantName}
                      </p>
                      <p className="mt-1 font-semibold text-navy truncate">
                        {v.rewardName}
                      </p>
                      <p className="mt-1 font-mono text-xs text-ink-dark">
                        {v.voucherCode}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_STYLES[v.status]}`}
                    >
                      {v.status}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
